import { Button, Col, Popconfirm, Row, Spin, theme } from 'antd';
import { FC } from 'react';
import { CloudUpload, ExclamationCircle } from 'react-bootstrap-icons';

import { DescriptionBlock } from '@/common/components';
import { BaseQueryParams } from '@/common/interfaces';
import { CommonDrawerTitle } from '@/common/layouts';
import { getDateFactory } from '@/common/utils';

import { useGetByIdSearchConsoleSitemapDomainsQuery } from '@/modules/Domains';

interface Props {
  searchConsoleDomainSitemap: BaseQueryParams<typeof useGetByIdSearchConsoleSitemapDomainsQuery>['data'] | null;
  isSitemapSubmitting: boolean;
  isSitemapDataRenewing: boolean;
  isSearchConsoleDomainSitemapLoading: boolean;
  handleGenerateSitemap: () => void;
  handleGetSitemap: () => void;
}

const isAvailable = <T extends boolean | string | Date | Record<string, unknown>[] | null | undefined>(
  value: T,
): value is NonNullable<T> => value !== null && value !== undefined;

export const SearchConsoleSitemapDossierView: FC<Props> = ({
  searchConsoleDomainSitemap,
  isSitemapSubmitting,
  isSitemapDataRenewing,
  isSearchConsoleDomainSitemapLoading,
  handleGenerateSitemap,
  handleGetSitemap,
}) => {
  const { token } = theme.useToken();

  return (
    <>
      <CommonDrawerTitle
        onlyExtra
        extra={
          <Popconfirm
            disabled={ isSitemapSubmitting }
            title="Завантажити sitemap в Search Console?"
            onConfirm={ handleGenerateSitemap }
            okType="danger"
            okText="Так"
            icon={ <ExclamationCircle color={ token.colorError }/> }
            placement="bottomRight"
          >
            <Button loading={ isSitemapSubmitting } disabled={ isSitemapSubmitting } icon={ <CloudUpload/> }>
              Завантажити
            </Button>
          </Popconfirm>
        }
      />

      {
        searchConsoleDomainSitemap
          ? <>
            <Row gutter={ [0, 16] }>
              <Col span={ 24 }>
                <Spin spinning={ isSitemapDataRenewing }>
                  { isAvailable(searchConsoleDomainSitemap?.path) && (
                    <DescriptionBlock items={ [['path', searchConsoleDomainSitemap.path]] }/>
                  ) }

                  { isAvailable(searchConsoleDomainSitemap?.type) && (
                    <DescriptionBlock items={ [['type', searchConsoleDomainSitemap.type]] }/>
                  ) }

                  { isAvailable(searchConsoleDomainSitemap?.warnings) && (
                    <DescriptionBlock items={ [['warnings', searchConsoleDomainSitemap.warnings]] }/>
                  ) }

                  { isAvailable(searchConsoleDomainSitemap?.errors) && (
                    <DescriptionBlock items={ [['errors', searchConsoleDomainSitemap.errors]] }/>
                  ) }

                  { isAvailable(searchConsoleDomainSitemap?.isPending) && (
                    <DescriptionBlock items={ [['isPending', searchConsoleDomainSitemap.isPending.toString()]] }/>
                  ) }

                  { isAvailable(searchConsoleDomainSitemap?.isSitemapsIndex) && (
                    <DescriptionBlock items={ [['isSitemapsIndex', searchConsoleDomainSitemap.isSitemapsIndex.toString()]] }/>
                  ) }

                  { isAvailable(searchConsoleDomainSitemap?.lastSubmitted) && (
                    <DescriptionBlock
                      items={ [
                        [
                          'lastSubmitted',
                          getDateFactory(searchConsoleDomainSitemap.lastSubmitted, 'Thursday, August 16, 2018 08:02', true),
                        ],
                      ] }
                    />
                  ) }

                  { isAvailable(searchConsoleDomainSitemap?.lastDownloaded) && (
                    <DescriptionBlock
                      items={ [
                        [
                          'lastDownloaded',
                          getDateFactory(searchConsoleDomainSitemap.lastDownloaded, 'Thursday, August 16, 2018 08:02', true),
                        ],
                      ] }
                    />
                  ) }

                  { isAvailable(searchConsoleDomainSitemap?.contents) && (
                    <DescriptionBlock
                      items={ [
                        [
                          'contents',
                          <>
                            <ul style={{ paddingLeft: 14 }}>
                              { searchConsoleDomainSitemap.contents.map((content, index) => (
                                <li key={ index }>
                                  <div>
                                    <b>type:</b>&nbsp;{ content.type?.toString() }
                                  </div>
                                  <div>
                                    <b>submitted:</b>&nbsp;{ content.submitted?.toString() }
                                  </div>
                                  <div>
                                    <b>indexed:</b>&nbsp;{ content.indexed?.toString() }
                                  </div>
                                </li>
                              )) }
                            </ul>
                          </>,
                        ],
                      ] }
                    />
                  ) }
                </Spin>
              </Col>
            </Row>
          </>
          : (
            <Button
              block
              size="large"
              loading={ isSearchConsoleDomainSitemapLoading }
              disabled={ isSearchConsoleDomainSitemapLoading }
              onClick={ handleGetSitemap }
            >
              Отримати sitemap
            </Button>
          )
          // : <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="Sitemap в Search Console відсутній"/>
      }
    </>
  );
};
